import type { Route } from "../../../utils/models/Route";
import { nearestAlongLine } from "./day-flags";
import { dayLine } from "./route-highlights";
import { activeStageEndDay, attachableDayNumbers } from "./stage-destinations";

/**
 * The days a place could be the end of, for "End day here" on a place's
 * sheet (MapDataPOIDetailDialog.vue). Pure.
 */

export interface DayEndChoice {
  dayNumber: number;
  /** The day's own name, when it has one. */
  name: string | null;
  /** How far the place is from the day's route, in metres (null without a line). */
  distanceM: number | null;
  /** The day to suggest: the selected route's, else the nearest. */
  suggested: boolean;
}

export function dayEndChoices(
  routes: { [routeKey: string]: Route },
  dayInfo: { [day: number]: { name?: string | null } | undefined } | null | undefined,
  activeRouteKey: string | null | undefined,
  coordinates: number[]
): DayEndChoice[] {
  const choices = attachableDayNumbers(routes).map(dayNumber => {
    const line = dayLine(routes, dayNumber);
    return {
      dayNumber,
      name: dayInfo?.[dayNumber]?.name?.trim() || null,
      distanceM: line ? nearestAlongLine(line, coordinates).offLineM : null,
      suggested: false,
    };
  });
  if (choices.length === 0) return choices;

  const active = activeStageEndDay(routes, activeRouteKey);
  const nearest = choices
    .filter(choice => choice.distanceM !== null)
    .sort((a, b) => a.distanceM! - b.distanceM!)[0];
  const suggestedDay = active ?? nearest?.dayNumber ?? choices[0].dayNumber;
  return choices.map(choice => ({ ...choice, suggested: choice.dayNumber === suggestedDay }));
}

/** The day "End day here" ends straight away — the only one there is — or null when there's a choice to make. */
export function onlyDayToEnd(choices: DayEndChoice[]): number | null {
  return choices.length === 1 ? choices[0].dayNumber : null;
}
